import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { Close, Delete, Edit, VisibilityOutlined } from "@mui/icons-material";
import React, { useEffect, useState } from "react";
import { collection, deleteDoc, doc, getDocs, orderBy, query, updateDoc } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { firestore, storage } from "../../..";
import { useFeedback } from "../../../providers/feedback/feedback";

type GalleryItem = {
  docId: string;
  title: string;
  programDate?: string;
  coverImage: string;
  albumUrl: string;
  createdAt?: string;
};

const EMPTY_FORM = {
  title: "",
  programDate: "",
  coverImage: "",
  albumUrl: "",
};

function normalizeUrl(value: string) {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return "";
  }

  if (
    trimmedValue.startsWith("http://") ||
    trimmedValue.startsWith("https://") ||
    trimmedValue.startsWith("/")
  ) {
    return trimmedValue;
  }

  return `https://${trimmedValue}`;
}

export default function ManageGalleryTab() {
  const { showSnackbar } = useFeedback();
  const [galleryItems, setGalleryItems] = useState<GalleryItem[]>([]);
  const [failedImages, setFailedImages] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [editingItem, setEditingItem] = useState<GalleryItem | null>(null);
  const [formValues, setFormValues] = useState(EMPTY_FORM);
  const [selectedCoverImage, setSelectedCoverImage] = useState<File | null>(null);

  async function fetchGalleryItems() {
    try {
      setIsLoading(true);
      const galleryQuery = query(
        collection(firestore, "gallery"),
        orderBy("createdAt", "desc"),
      );
      const snapshot = await getDocs(galleryQuery);
      setGalleryItems(
        snapshot.docs.map((galleryDoc) => ({
          docId: galleryDoc.id,
          ...(galleryDoc.data() as Omit<GalleryItem, "docId">),
          coverImage: normalizeUrl((galleryDoc.data() as Omit<GalleryItem, "docId">).coverImage || ""),
          albumUrl: normalizeUrl((galleryDoc.data() as Omit<GalleryItem, "docId">).albumUrl || ""),
        })),
      );
    } catch (error) {
      console.error("Error fetching gallery items", error);
      showSnackbar("Failed to load gallery items");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchGalleryItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleEdit(item: GalleryItem) {
    setEditingItem(item);
    setFormValues({
      title: item.title || "",
      programDate: item.programDate || "",
      coverImage: item.coverImage || "",
      albumUrl: item.albumUrl || "",
    });
    setSelectedCoverImage(null);
  }

  async function handleUpdate() {
    if (!editingItem) {
      return;
    }

    try {
      let finalCoverImage = formValues.coverImage.trim();

      if (selectedCoverImage) {
        const storageRef = ref(
          storage,
          `gallery/${Date.now()}-${selectedCoverImage.name}`,
        );
        await uploadBytes(storageRef, selectedCoverImage);
        finalCoverImage = await getDownloadURL(storageRef);
      }

      await updateDoc(doc(firestore, "gallery", editingItem.docId), {
        ...formValues,
        coverImage: normalizeUrl(finalCoverImage),
        albumUrl: normalizeUrl(formValues.albumUrl),
        updatedAt: new Date().toISOString(),
      });
      showSnackbar("Gallery item updated successfully");
      setEditingItem(null);
      setFormValues(EMPTY_FORM);
      setSelectedCoverImage(null);
      fetchGalleryItems();
    } catch (error) {
      console.error("Error updating gallery item", error);
      showSnackbar("Failed to update gallery item");
    }
  }

  async function handleDelete(item: GalleryItem) {
    if (!window.confirm(`Delete "${item.title}"?`)) {
      return;
    }

    try {
      await deleteDoc(doc(firestore, "gallery", item.docId));
      showSnackbar("Gallery item deleted successfully");
      fetchGalleryItems();
    } catch (error) {
      console.error("Error deleting gallery item", error);
      showSnackbar("Failed to delete gallery item");
    }
  }

  if (isLoading) {
    return (
      <Box py={6} textAlign="center">
        <Typography>Loading gallery items...</Typography>
      </Box>
    );
  }

  if (!galleryItems.length) {
    return (
      <Box py={6} textAlign="center">
        <Typography>No gallery items available.</Typography>
      </Box>
    );
  }

  return (
    <Box mt={3}>
      <Grid container spacing={3}>
        {galleryItems.map((item) => (
          <Grid item xs={12} sm={6} md={6} lg={4} key={item.docId}>
            <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
              <Box
                sx={{
                  width: "100%",
                  height: 260,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "#ffffff",
                  borderBottom: "1px solid rgba(56, 136, 194, 0.12)",
                  p: 2,
                }}
              >
                {failedImages[item.docId] || !item.coverImage ? (
                  <Box textAlign="center" px={2}>
                    <Typography variant="body2" fontWeight={700}>
                      Image unavailable
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Edit this item to upload a working cover image.
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    component="img"
                    src={item.coverImage}
                    alt={item.title}
                    loading="lazy"
                    onError={() =>
                      setFailedImages((prev) => ({ ...prev, [item.docId]: true }))
                    }
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      objectPosition: "center center",
                    }}
                  />
                )}
              </Box>
              <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1.5, flexGrow: 1 }}>
                <Typography variant="h6" fontWeight={700}>
                  {item.title}
                </Typography>
                {item.programDate ? (
                  <Typography variant="body2" color="text.secondary">
                    {new Date(item.programDate).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                  </Typography>
                ) : null}
                <Box display="flex" gap={1} flexWrap="wrap">
                  <Button
                    variant="outlined"
                    startIcon={<VisibilityOutlined />}
                    onClick={() => window.open(normalizeUrl(item.albumUrl), "_blank", "noopener,noreferrer")}
                  >
                    View
                  </Button>
                  <Button variant="outlined" startIcon={<Edit />} onClick={() => handleEdit(item)}>
                    Edit
                  </Button>
                  <Button color="error" variant="outlined" startIcon={<Delete />} onClick={() => handleDelete(item)}>
                    Delete
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={Boolean(editingItem)} onClose={() => setEditingItem(null)} fullWidth maxWidth="sm">
        <DialogTitle>Edit Gallery Item</DialogTitle>
        <DialogContent>
          <Box mt={1} display="flex" flexDirection="column" gap={2}>
            <TextField
              fullWidth
              label="Program Name"
              value={formValues.title}
              onChange={(event) => setFormValues((prev) => ({ ...prev, title: event.target.value }))}
            />
            <TextField
              fullWidth
              label="Program Date"
              type="date"
              value={formValues.programDate}
              onChange={(event) => setFormValues((prev) => ({ ...prev, programDate: event.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              label="Cover Image URL"
              value={formValues.coverImage}
              onChange={(event) => {
                setFormValues((prev) => ({ ...prev, coverImage: event.target.value }));
                if (event.target.value) {
                  setSelectedCoverImage(null);
                }
              }}
            />
            <FormControl fullWidth>
              <FormLabel>Upload New Cover Image</FormLabel>
              <input
                type="file"
                accept="image/*"
                style={{
                  border: "1px solid rgb(181, 176, 176)",
                  width: "96%",
                  paddingLeft: 8,
                  paddingRight: 8,
                  paddingTop: 16,
                  paddingBottom: 16,
                  borderRadius: 5,
                  marginTop: 8,
                }}
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    setSelectedCoverImage(e.target.files[0]);
                    setFormValues((prev) => ({ ...prev, coverImage: "" }));
                  }
                }}
              />
              {selectedCoverImage ? (
                <Box mt={2} position="relative" display="inline-block">
                  <img
                    src={URL.createObjectURL(selectedCoverImage)}
                    alt="Updated gallery cover preview"
                    style={{
                      maxWidth: "220px",
                      maxHeight: "180px",
                      borderRadius: 8,
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => setSelectedCoverImage(null)}
                    style={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      color: "#ffffff",
                      backgroundColor: "rgba(0,0,0,0.5)",
                    }}
                  >
                    <Close />
                  </IconButton>
                </Box>
              ) : formValues.coverImage ? (
                <Box mt={2}>
                  <Box
                    component="img"
                    src={normalizeUrl(formValues.coverImage)}
                    alt="Current gallery cover"
                    sx={{
                      maxWidth: 220,
                      maxHeight: 180,
                      borderRadius: 2,
                      objectFit: "contain",
                    }}
                  />
                </Box>
              ) : null}
            </FormControl>
            <TextField
              fullWidth
              label="Google Photos Link"
              value={formValues.albumUrl}
              onChange={(event) => setFormValues((prev) => ({ ...prev, albumUrl: event.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEditingItem(null);
              setSelectedCoverImage(null);
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button onClick={handleUpdate} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
